/**
 * Staff impersonation of a pax customer.
 *
 * A staff member with admin / super_admin / manager role can issue a signed
 * impersonation token (via `pax-impersonate-issue` edge function) and open
 * the pax portal as if they were that customer. This file owns:
 *
 *   - sessionStorage persistence of the token + display metadata
 *   - the URL "?as=<token>" handshake when the impersonate tab opens
 *   - helpers for the pax portal to detect impersonation and:
 *       a) pass the token to every pax edge function
 *       b) render the impersonation banner
 *       c) suppress analytics events so the impersonation session doesn't
 *          pollute customer activity dashboards
 *
 * The token is HMAC-SHA256 signed by `pax-impersonate-issue` with the same
 * MAGIC_LINK_SECRET we use for magic-link sessions. Pax edge functions
 * verify it via `_shared/pax-auth.ts` and resolve the target customer_id
 * from the `sub` claim.
 *
 * Audit: every issued token is logged to `pax_activity_events` as
 * `impersonation_issued` with the staff user_id, customer_id, and TTL.
 */

const STORAGE_KEY = 'pax_impersonate';
const URL_PARAM = 'as';

export interface ImpersonateSession {
  token: string;
  /** Customer id the staff member is viewing as. */
  customerId: string;
  /** Human-readable name for the banner. */
  customerName: string | null;
  customerEmail: string | null;
  /** ISO timestamp when the token expires. */
  expiresAt: string;
  /** When this device first stored the session. */
  startedAt: string;
}

const isBrowser = (): boolean => typeof window !== 'undefined' && !!window.sessionStorage;

export function getImpersonateSession(): ImpersonateSession | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonateSession;
    if (!parsed?.token || !parsed?.customerId || !parsed?.expiresAt) return null;
    // Expired? Drop and report no session.
    if (new Date(parsed.expiresAt).getTime() < Date.now()) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveImpersonateSession(s: ImpersonateSession): void {
  if (!isBrowser()) return;
  try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

export function clearImpersonateSession(): void {
  if (!isBrowser()) return;
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/**
 * If the URL contains `?as=<token>`, look up the token's metadata (by
 * decoding the JWT body — signature verification happens server-side every
 * call), persist the impersonation session, then strip the URL param so
 * browser history doesn't leak the token.
 */
export function ingestImpersonateFromUrl(): ImpersonateSession | null {
  if (!isBrowser()) return null;
  const url = new URL(window.location.href);
  const token = url.searchParams.get(URL_PARAM);
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadPart = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payloadPart + '='.repeat((4 - (payloadPart.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded)) as {
      sub?: string; actor?: string; actor_email?: string;
      customer_name?: string | null; customer_email?: string | null;
      exp?: number; kind?: string;
    };
    if (decoded.kind !== 'pax_impersonate' || !decoded.sub || !decoded.exp) return null;
    if (decoded.exp * 1000 < Date.now()) return null;

    const session: ImpersonateSession = {
      token,
      customerId: decoded.sub,
      // Customer display fields are embedded in the token by
      // pax-impersonate-issue so the banner can render the name without
      // an extra DB query (RLS on customers blocks the anon client).
      customerName: decoded.customer_name ?? null,
      customerEmail: decoded.customer_email ?? null,
      expiresAt: new Date(decoded.exp * 1000).toISOString(),
      startedAt: new Date().toISOString(),
    };
    saveImpersonateSession(session);

    // Strip the token from the visible URL so browser history / shoulder-
    // surfers don't see it. Replace the entry with the same path minus ?as.
    url.searchParams.delete(URL_PARAM);
    window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);

    return session;
  } catch {
    return null;
  }
}

/**
 * Returns true if the current session is an impersonation session — useful
 * for guarding analytics calls so staff "viewing as customer" doesn't write
 * activity events that look like customer behaviour.
 */
export function isImpersonating(): boolean {
  return getImpersonateSession() !== null;
}

/**
 * Returns the impersonate token to pass through to pax edge functions, OR
 * undefined when not impersonating. Pax functions accept this in their
 * body as `impersonate_token` and verify it via `_shared/pax-auth.ts`.
 */
export function getImpersonateToken(): string | undefined {
  return getImpersonateSession()?.token ?? undefined;
}
